import { PermissionGuard } from '@/features/auth';
import { Alert, Button } from '@/shared/components';
import { useDeactivateUserMutation } from '../hooks/useDeactivateUserMutation';
import { useReactivateUserMutation } from '../hooks/useReactivateUserMutation';
import { useDeleteUserMutation } from '../hooks/useDeleteUserMutation';

interface Props {
  userId: string;
  status: 'active' | 'inactive';
}

export function UserStatusPanel({ userId, status }: Props) {
  const deactivateMutation = useDeactivateUserMutation();
  const reactiveMutation = useReactivateUserMutation();
  const deleteMutation = useDeleteUserMutation();

  const handleStatusChange = () => {
    if (status === 'active') {
      if (!window.confirm('Are you sure you want to deactivate this user? This will sign the user out of all active sessions and prevent them from signing in again until reactivated.')) {
        return;
      }
      deactivateMutation.mutate(userId);
    } else {
      if (!window.confirm('Are you sure you want to reactivate this user? This will allow the user to sign in again.')) {
        return;
      }
      reactiveMutation.mutate(userId);
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      return;
    }
    deleteMutation.mutate(userId);
  };

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        <div>
          <h3 className="font-semibold mb-2">{status === 'active' ? 'Deactivate User' : 'Reactivate User'}</h3>
          <div className="flex items-start gap-4 justify-between">
            <p className="text-sm text-mp-muted">
              {status === 'active'
                ? 'Deactivating the user will sign them out of all active sessions and prevent them from signing in again until reactivated.'
                : 'Reactivating the user will allow them to sign in again.'}
            </p>
            <PermissionGuard permission={{ resource: 'user', action: 'deactivate' }}>
              <Button
                size="sm"
                variant={`${status === 'active' ? 'danger' : 'primary'}`}
                onClick={handleStatusChange}
                disabled={reactiveMutation.isPending || deactivateMutation.isPending}
                className="whitespace-nowrap"
              >
                {status === 'active'
                  ? 'Deactivate User'
                  : 'Reactivate User'}
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Errors surface via the hook-level handleUserMutationError toast — no inline alert. */}

        {reactiveMutation.isSuccess && (
          <Alert variant="success">
            <span>User has been reactivated successfully.</span>
          </Alert>
        )}
        {deactivateMutation.isSuccess && (
          <Alert variant="success">
            <span>User has been deactivated successfully.</span>
          </Alert>
        )}
      </div>
      {status === 'inactive' && (
        <div className="border-t border-mp-border mt-8 pt-6 max-w-2xl space-y-2">
          <div>
            <h3 className="font-semibold mb-2">Delete User</h3>
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Deleting the user will permanently remove their account and all associated data. This action cannot be undone.
              </p>
              <PermissionGuard permission={{ resource: 'user', action: 'delete' }}>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="whitespace-nowrap"
                >
                  Delete User
                </Button>
              </PermissionGuard>
            </div>
          </div>

          {/* Errors surface via the hook-level handleUserMutationError toast — no inline alert. */}

          {deleteMutation.isSuccess && (
            <Alert variant="success">
              <span>User has been deleted successfully.</span>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
